/**
 * Configuración de categorías de productos.
 * Cada categoría tiene una posición configurada en el ID de producto.
 * Formato del ID: X-XXX-XXX-XX-X-XXXX
 * - Posición 1: CPP = CrazyPlantPots (ej: E-CPP-CM-CZ-S-0001)
 * - Posición 2: TB, NC, CM, CJ, MC (ej: E-DAC-TB-B0-M-0001 → TB = Totebag)
 */

export const CATEGORY_CODES = {
  CPP: {
    id: 'crazyplantpots',
    code: 'CPP',
    name: 'CrazyPlantPot',
    namePlural: 'CrazyPlantPots',
    color: '#9b59b6',
    position: 1
  },
  TB: {
    id: 'totebags',
    code: 'TB',
    name: 'Totebag',
    namePlural: 'Totebags',
    color: '#ff6b8b',
    position: 2
  },
  NC: {
    id: 'neceser',
    code: 'NC',
    name: 'Neceser',
    namePlural: 'Neceseres',
    color: '#6b5bff',
    position: 2
  },
  CJ: {
    id: 'cojines',
    code: 'CJ',
    name: 'Cojín',
    namePlural: 'Cojines',
    color: '#ffd93d',
    position: 2
  },
  MC: {
    id: 'macetas',
    code: 'MC',
    name: 'Maceta',
    namePlural: 'Macetas',
    color: '#6bcb77',
    position: 2
  }
};

/**
 * Opciones de ordenamiento disponibles
 */
export const SORT_OPTIONS = [
  { id: 'default', label: 'Más reciente', field: null, order: null },
  { id: 'price_asc', label: 'Precio: Menor a Mayor', field: 'price', order: 'asc' },
  { id: 'price_desc', label: 'Precio: Mayor a Menor', field: 'price', order: 'desc' }
];

/**
 * Extrae el código de categoría del ID de producto
 * @param {string} productId - ID del producto (ej: "E-DAC-TB-B0-M-0001")
 * @param {number|null} position - Posición específica a extraer, o null para posición 2 por defecto
 * @returns {string|null} - Código de categoría (ej: "TB") o null si no se encuentra
 */
export const getCategoryCodeFromProductId = (productId, position = null) => {
  if (!productId || typeof productId !== 'string') return null;
  const parts = productId.split('-');

  if (position !== null) {
    return parts.length > position ? parts[position] : null;
  }

  // Por defecto, retorna posición 2 (comportamiento original)
  return parts.length >= 3 ? parts[2] : null;
};

/**
 * Obtiene la configuración de categoría para un producto
 * Busca en todas las categorías según su posición configurada
 * @param {string} productId - ID del producto
 * @returns {Object|null} - Objeto de configuración de categoría o null
 */
export const getCategoryFromProductId = (productId) => {
  if (!productId || typeof productId !== 'string') return null;
  const parts = productId.split('-');

  // Buscar en todas las categorías según su posición configurada
  for (const [code, config] of Object.entries(CATEGORY_CODES)) {
    const pos = config.position || 2;
    if (parts[pos] === code) {
      return config;
    }
  }
  return null;
};

/**
 * Obtiene todas las categorías disponibles como array
 * @returns {Array} - Array de objetos de categoría
 */
export const getAllCategories = () => {
  return Object.values(CATEGORY_CODES);
};

/**
 * Obtiene las categorías que tienen productos
 * Considera la posición configurada de cada categoría
 * @param {Array} products - Array de productos
 * @returns {Array} - Categorías con al menos un producto
 */
export const getAvailableCategories = (products) => {
  if (!products || !Array.isArray(products)) return [];

  const categoryCodes = new Set();

  products.forEach(product => {
    const parts = product.id?.split('-') || [];

    Object.entries(CATEGORY_CODES).forEach(([code, config]) => {
      const pos = config.position || 2;
      if (parts[pos] === code) {
        categoryCodes.add(code);
      }
    });
  });

  return Array.from(categoryCodes).map(code => CATEGORY_CODES[code]);
};

/**
 * Obtiene una opción de ordenamiento por su ID
 * @param {string} sortId - ID del ordenamiento
 * @returns {Object} - Opción de ordenamiento o la opción por defecto
 */
export const getSortOptionById = (sortId) => {
  return SORT_OPTIONS.find(option => option.id === sortId) || SORT_OPTIONS[0];
};
